import React, { Fragment, useContext, useEffect, useState, useMemo } from 'react';
import { Link, useMatch, useNavigate } from 'react-router-dom';
import commonArray from '../../components/common/commonArray';
import BreadCrumb from '../../components/common/BreadCrumb';
import { GlobalContext, handleError } from '../../context/Provider';
import profile_img1 from '../../assets/images/pages/defaultImage.jpg';
import henceforthApi from '../../utils/henceforthApi';
import Spinner from '../../components/common/spinner';
import Swal from 'sweetalert2';
import moment from 'moment';
import { numberWithCommas } from '../../utils/validations';
import { stateType } from './productInterface';
import SectionCard from './components/SectionCard';
import { Button } from '../../components/ui/button';
import {
    ArrowLeft,
    Plus,
    Pencil,
    Trash2,
    Copy,
    Star,
    ChevronLeft,
    ChevronRight,
    ChevronDown,
    ChevronUp,
    CheckCircle2,
    Building2,
    Tag,
    Layers,
    Sparkles,
    ShieldCheck,
    HelpCircle,
    Boxes,
    Truck,
    Globe,
    AlertTriangle,
    Package,
    ZoomIn,
    X,
    Check,
    Clock,
    MapPin,
} from 'lucide-react';

const ViewProductDetail: React.FC = () => {
    const { authState, language_data, authDispatch } = useContext(GlobalContext);
    const match = useMatch('/product/:id');
    const navigate = useNavigate();
    const productId = match?.params.id || '';

    const breadCrumbPath = [
        {
            name: `${language_data?.[authState.lang]?.common_home || 'Home'}`,
            url: `/`,
            active: '',
        },
        {
            name: `${language_data?.[authState.lang]?.common_product || 'Product'} ${language_data?.[authState.lang]?.common_list || 'List'
                }`,
            url: `/products/1`,
            active: '',
        },
        {
            name: `${language_data?.[authState.lang]?.common_product || 'Product'} ${language_data?.[authState.lang]?.common_deatil || 'Detail'
                }`,
            url: ``,
            active: 'not-allowed',
        },
    ];

    const [loading, setLoading] = useState<boolean>(true);
    const [variants, setVariants] = useState<Array<any>>([]);
    const [highlights, setHighlights] = useState<Array<any>>([]);
    const [services, setServices] = useState<Array<any>>([]);
    const [specification, setspecification] = useState<Array<any>>([]);
    const [faq, setFaq] = useState<Array<any>>([]);
    const [delivery, setDelivery] = useState<Array<any>>([]);
    const [isTogglingDelivery, setIsTogglingDelivery] = useState<boolean>(false);
    const [activeImageIndex, setActiveImageIndex] = useState<number>(0);
    const [showMoreDesc, setShowMoreDesc] = useState<boolean>(false);
    const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
    const [isLightboxOpen, setIsLightboxOpen] = useState<boolean>(false);
    const [copiedSku, setCopiedSku] = useState<boolean>(false);
    const [activeSection, setActiveSection] = useState<string>('all');

    const [state, setState] = useState<stateType>({
        _id: '',
        name: '',
        description: '',
        product_type: '',
        added_by: { _id: '', name: '' },
        category_id: { _id: '', name: '' },
        subcategory_id: { _id: '', name: '' },
        sub_subcategory_id: { _id: '', name: '' },
        brand_id: { _id: '', name: '' },
        variant_type: '',
        variant_value: { label: '', value: '' },
        images: [],
        quantity: '',
        price: 0,
        prodct_id: '',
        discount_percantage: '',
        tax_percentage: 0,
        discount: '',
        discount_price: 0,
        total_reviews: '',
        total_ratings: '',
        ratings: [],
        is_delivery_available: false,
    });

    const productDetails = async () => {
        try {
            const productRes = (await henceforthApi.Product.getProductDetails(productId)).data;
            setState(productRes);
        } catch (err: any) {
            console.error(err?.response?.body?.error_description || err?.message || err);
            handleError(err, '', authDispatch);
        }
    };

    const productOtherDetails = async (apiUrl: string) => {
        try {
            const isDelivery = apiUrl === commonArray.productOtherDetails[5];
            const apiRes = (
                await henceforthApi.Product.getProductDetailsAllDynamics(
                    apiUrl,
                    isDelivery ? productId : '',
                    !isDelivery ? productId : ''
                )
            ).data.data;

            if (apiUrl === commonArray.productOtherDetails[0]) setHighlights(apiRes || []);
            else if (apiUrl === commonArray.productOtherDetails[1]) setspecification(apiRes || []);
            else if (apiUrl === commonArray.productOtherDetails[2]) setVariants(apiRes || []);
            else if (apiUrl === commonArray.productOtherDetails[3]) {
                setFaq(
                    Array.isArray(apiRes)
                        ? apiRes.map((item: any) => ({ ...item, readMore: false }))
                        : []
                );
            } else if (apiUrl === commonArray.productOtherDetails[4]) setServices(apiRes || []);
            else if (apiUrl === commonArray.productOtherDetails[5]) setDelivery(apiRes || []);
        } catch (err: any) {
            console.error(err?.response?.body?.error_description || err?.message || err);
            handleError(err, '', authDispatch);
        }
    };

    const loadAllData = async () => {
        setLoading(true);
        try {
            await Promise.all([
                productDetails(),
                ...commonArray.productOtherDetails.map((url) => productOtherDetails(url)),
            ]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (productId) {
            loadAllData();
            setActiveImageIndex(0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productId]);

    const toggleDelivery = async (checked: boolean) => {
        setIsTogglingDelivery(true);
        const items = {
            _id: productId,
            is_delivery_available: checked,
            language: 'ENGLISH',
        };
        try {
            await henceforthApi.Product.editProduct(items);
            setState((prev) => ({ ...prev, is_delivery_available: checked }));
        } catch (error) {
            console.error(error);
        } finally {
            setIsTogglingDelivery(false);
        }
    };

    const deleteSpecificDetail = async (select: string, id: any) => {
        Swal.fire({
            title: `${language_data?.[authState.lang]?.common_are_you_sure || 'Are you sure'}?`,
            text: `${language_data?.[authState.lang]?.common_are_sure_want_to || 'Are you sure you want to'} ${language_data?.[authState.lang]?.common_delete || 'delete'
                } this ${select}?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#1AA7DE',
            cancelButtonColor: '#ef4444',
            cancelButtonText: `${language_data?.[authState.lang]?.common_cancel || 'Cancel'}`,
            confirmButtonText: `${language_data?.[authState.lang]?.common_yes || 'Yes'}, ${language_data?.[authState.lang]?.common_delete || 'delete'
                }`,
            customClass: {
                popup: 'rounded-2xl',
                confirmButton: 'rounded-xl font-bold px-4 py-2 text-sm',
                cancelButton: 'rounded-xl font-bold px-4 py-2 text-sm',
            },
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    if (commonArray.productOtherDetails.includes(select)) {
                        await henceforthApi.Product.deleteProductDetailsDynamically(select, id);
                        await productOtherDetails(select);
                    } else if (select === 'product') {
                        await henceforthApi.Product.editProduct({
                            _id: id,
                            is_deleted: true,
                        });
                        navigate('/products/1');
                    }
                    Swal.fire({
                        icon: 'success',
                        title: `${language_data?.[authState.lang]?.common_deleted || 'Deleted!'}`,
                        text: `${language_data?.[authState.lang]?.common_your || 'Your'} ${select} has been removed.`,
                        timer: 2000,
                        showCancelButton: false,
                        showConfirmButton: false,
                        customClass: { popup: 'rounded-2xl' },
                    });
                } catch (err: any) {
                    handleError(err, 'active', authDispatch);
                    console.error(err?.response?.body?.error_description || err?.message || err);
                }
            }
        });
    };

    const toggleFaqReadMore = (index: number) => {
        setFaq((prev) =>
            prev.map((item, idx) => (idx === index ? { ...item, readMore: !item.readMore } : item))
        );
    };

    const handleCopySku = () => {
        const sku = state.prodct_id || state._id;
        if (sku) {
            navigator.clipboard.writeText(sku);
            setCopiedSku(true);
            setTimeout(() => setCopiedSku(false), 2000);
        }
    };

    const scrollToSection = (sectionId: string) => {
        setActiveSection(sectionId);
        const element = document.getElementById(sectionId);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const imagesList = useMemo(() => {
        if (Array.isArray(state.images) && state.images.length > 0) {
            return state.images;
        }
        return [];
    }, [state.images]);

    const activeImageUrl = useMemo(() => {
        if (imagesList.length > 0) {
            return `${henceforthApi?.API_FILE_ROOT_ORIGINAL}${imagesList[activeImageIndex]}`;
        }
        return profile_img1;
    }, [imagesList, activeImageIndex]);

    const averageRating = useMemo(() => {
        if (Array.isArray(state.ratings) && state.ratings.length > 0) {
            const sum = state.ratings.reduce((acc, curr) => acc + (Number(curr?.ratings) || 0), 0);
            return (sum / state.ratings.length).toFixed(1);
        }
        return state.total_ratings ? Number(state.total_ratings).toFixed(1) : null;
    }, [state.ratings, state.total_ratings]);

    const ratingCounts = useMemo(() => {
        const counts = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
        if (Array.isArray(state.ratings)) {
            state.ratings.forEach((r) => {
                const val = Math.min(5, Math.max(1, Math.round(Number(r?.ratings) || 0))) as 1 | 2 | 3 | 4 | 5;
                if (counts[val] !== undefined) counts[val]++;
            });
        }
        return counts;
    }, [state.ratings]);

    const stockQuantity = Number(state.quantity) || 0;
    const isInStock = stockQuantity > 0;
    const discountPercent = Number(state.discount_percantage) || 0;
    const originalPrice = Number(state.price) || 0;
    const discountedPrice = discountPercent > 0 ? originalPrice * (1 - discountPercent / 100) : originalPrice;
    const taxAmount = (originalPrice * (Number(state.tax_percentage) || 0)) / 100;

    return (
        <Fragment>
            <BreadCrumb pathNameDeclare={breadCrumbPath} />

            {loading ? (
                <div className="min-h-[70vh] flex flex-col items-center justify-center py-16 gap-3">
                    <Spinner color="text-[#1AA7DE]" />
                    <p className="text-xs text-slate-500 font-semibold tracking-wide animate-pulse">
                        Loading product details...
                    </p>
                </div>
            ) : (
                <div className="w-full px-4 sm:px-6 lg:px-8 py-5 space-y-5">
                    {/* Top Sticky Header & Actions Bar */}
                    <div className="bg-white/95 backdrop-blur-md sticky top-0 z-20 py-3 px-4 rounded-2xl border border-slate-200/80 shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="flex flex-wrap items-center gap-2">
                            {/* Copyable SKU Chip */}
                            <button
                                type="button"
                                onClick={handleCopySku}
                                className="inline-flex items-center gap-1.5 font-mono text-xs font-bold px-2.5 py-1 rounded-lg bg-slate-100 hover:bg-slate-200/70 text-slate-700 border border-slate-200/80 transition-all cursor-pointer group"
                                title="Click to copy SKU / Product ID"
                            >
                                <span>#{state.prodct_id || state._id?.slice(-8) || 'N/A'}</span>
                                {copiedSku ? (
                                    <Check className="w-3 h-3 text-emerald-600" />
                                ) : (
                                    <Copy className="w-3 h-3 text-slate-400 group-hover:text-slate-600" />
                                )}
                            </button>

                            {/* Stock Status Badge */}
                            <span
                                className={`inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-lg border ${isInStock
                                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200/80'
                                    : 'bg-rose-50 text-rose-700 border-rose-200/80'
                                    }`}
                            >
                                <span
                                    className={`w-1.5 h-1.5 rounded-full ${isInStock ? 'bg-emerald-500 animate-pulse' : 'bg-rose-500'
                                        }`}
                                />
                                {isInStock
                                    ? `${stockQuantity} In Stock`
                                    : language_data?.[authState.lang]?.common_out_of_stock || 'Out of Stock'}
                            </span>

                            {/* Worldwide Delivery Status Pill */}
                            {state.is_delivery_available ? (
                                <span className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-lg bg-sky-50 text-[#1AA7DE] border border-sky-200/70">
                                    <Globe className="w-3 h-3" />
                                    Worldwide Delivery
                                </span>
                            ) : (
                                <span className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-lg bg-slate-100 text-slate-600 border border-slate-200/70">
                                    <Truck className="w-3 h-3 text-slate-400" />
                                    Local Shipping
                                </span>
                            )}
                        </div>

                        {/* Action Buttons using common Button component */}
                        <div className="flex items-center gap-2 flex-wrap">
                            <Button
                                asChild
                                variant="outline"

                                className="gap-1.5"
                            >
                                <Link to={`/product/clone/${productId}`}>
                                    <Copy className="w-3.5 h-3.5" />
                                    <span>{language_data?.[authState.lang]?.common_clone || 'Clone'}</span>
                                </Link>
                            </Button>

                            <Button
                                type="button"
                                variant="destructive"

                                onClick={() => deleteSpecificDetail('product', productId)}
                                className="gap-1.5"
                            >
                                <Trash2 className="w-3.5 h-3.5" />
                                <span>{language_data?.[authState.lang]?.common_delete || 'Delete'}</span>
                            </Button>

                            <Button
                                asChild
                                variant="default"

                                className="gap-1.5"
                            >
                                <Link to={`/product/${productId}/edit`}>
                                    <Pencil className="w-3.5 h-3.5" />
                                    <span>{language_data?.[authState.lang]?.common_edit || 'Edit Product'}</span>
                                </Link>
                            </Button>
                        </div>
                    </div>

                    {/* Quick Jump Anchor Tabs using common Button */}
                    <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-none">
                        <Button
                            type="button"
                            variant={activeSection === 'hero-section' ? 'default' : 'outline'}

                            onClick={() => scrollToSection('hero-section')}
                        >
                            Overview
                        </Button>
                        <Button
                            type="button"
                            variant={activeSection === 'highlights-section' ? 'default' : 'outline'}

                            onClick={() => scrollToSection('highlights-section')}
                        >
                            Highlights ({highlights.length})
                        </Button>
                        <Button
                            type="button"
                            variant={activeSection === 'specifications-section' ? 'default' : 'outline'}

                            onClick={() => scrollToSection('specifications-section')}
                        >
                            Specifications ({specification.length})
                        </Button>
                        <Button
                            type="button"
                            variant={activeSection === 'variations-section' ? 'default' : 'outline'}

                            onClick={() => scrollToSection('variations-section')}
                        >
                            Variations ({variants.length})
                        </Button>
                        <Button
                            type="button"
                            variant={activeSection === 'delivery-section' ? 'default' : 'outline'}

                            onClick={() => scrollToSection('delivery-section')}
                        >
                            Delivery & Services
                        </Button>
                        <Button
                            type="button"
                            variant={activeSection === 'faq-section' ? 'default' : 'outline'}

                            onClick={() => scrollToSection('faq-section')}
                        >
                            FAQs ({faq.length})
                        </Button>
                        <Button
                            type="button"
                            variant={activeSection === 'ratings-section' ? 'default' : 'outline'}

                            onClick={() => scrollToSection('ratings-section')}
                        >
                            Reviews ({state.ratings?.length || 0})
                        </Button>
                    </div>

                    {/* Delivery Warning Banner */}
                    {!state.is_delivery_available && delivery.length === 0 && (
                        <div className="p-4 rounded-2xl bg-gradient-to-r from-amber-50 to-orange-50/50 border border-amber-200/80 flex items-start sm:items-center justify-between gap-3 shadow-2xs">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
                                    <AlertTriangle className="w-5 h-5" />
                                </div>
                                <div>
                                    <h4 className="text-xs sm:text-sm font-bold text-amber-900 m-0">
                                        Shipping Not Configured
                                    </h4>
                                    <p className="text-xs text-amber-700 font-medium m-0 mt-0.5">
                                        {language_data?.[authState.lang]
                                            ?.common_please_add_delivery_if_you_want_to_show_your_product_in_marketplace ||
                                            'Please configure delivery zones or enable worldwide shipping to make this product visible in the marketplace.'}
                                    </p>
                                </div>
                            </div>
                            <Button
                                type="button"
                                variant="default"

                                onClick={() => toggleDelivery(true)}
                                disabled={isTogglingDelivery}
                            >
                                Enable Worldwide
                            </Button>
                        </div>
                    )}

                    {/* Main Product Hero Card - Spanning Full Width */}
                    <div id="hero-section" className="bg-white rounded-2xl border border-slate-100 shadow-xs p-4 sm:p-6 lg:p-7 w-full">
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
                            {/* Left Column: Image Showcase (5 cols) */}
                            <div className="lg:col-span-5 space-y-3">
                                <div className="relative aspect-square w-full rounded-2xl border border-slate-100 bg-radial from-slate-50 via-slate-50/80 to-slate-100/50 p-4 flex items-center justify-center overflow-hidden shadow-2xs group">
                                    <img
                                        src={activeImageUrl}
                                        className="w-full h-full object-contain mix-blend-multiply transition-transform duration-300 group-hover:scale-105 cursor-pointer"
                                        alt={state.name || 'Product'}
                                        onClick={() => setIsLightboxOpen(true)}
                                    />

                                    {/* Zoom overlay button */}
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="icon"
                                        onClick={() => setIsLightboxOpen(true)}
                                        className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity"
                                        title="View fullscreen image"
                                    >
                                        <ZoomIn className="w-4 h-4" />
                                    </Button>

                                    {/* Discount tag badge on image */}
                                    {discountPercent > 0 && (
                                        <div className="absolute top-3 left-3 px-2.5 py-1 rounded-xl bg-rose-500 text-white font-black text-xs shadow-sm flex items-center gap-1">
                                            <span>-{discountPercent}%</span>
                                            <span className="text-[10px] font-semibold opacity-90 uppercase">Off</span>
                                        </div>
                                    )}

                                    {/* Image Counter */}
                                    {imagesList.length > 1 && (
                                        <div className="absolute bottom-3 right-3 px-2.5 py-1 rounded-lg bg-slate-900/75 text-white text-[11px] font-bold backdrop-blur-xs">
                                            {activeImageIndex + 1} / {imagesList.length}
                                        </div>
                                    )}

                                    {/* Navigation Arrows */}
                                    {imagesList.length > 1 && (
                                        <>
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="icon"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setActiveImageIndex((prev) =>
                                                        prev === 0 ? imagesList.length - 1 : prev - 1
                                                    );
                                                }}
                                                className="absolute left-2.5 top-1/2 -translate-y-1/2 rounded-full opacity-80 group-hover:opacity-100"
                                                aria-label="Previous image"
                                            >
                                                <ChevronLeft className="w-4 h-4" />
                                            </Button>
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="icon"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setActiveImageIndex((prev) =>
                                                        prev === imagesList.length - 1 ? 0 : prev + 1
                                                    );
                                                }}
                                                className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-full opacity-80 group-hover:opacity-100"
                                                aria-label="Next image"
                                            >
                                                <ChevronRight className="w-4 h-4" />
                                            </Button>
                                        </>
                                    )}
                                </div>

                                {/* Thumbnails Row */}
                                {imagesList.length > 1 && (
                                    <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-thin">
                                        {imagesList.map((img, idx) => (
                                            <button
                                                key={idx}
                                                type="button"
                                                onClick={() => setActiveImageIndex(idx)}
                                                className={`relative w-14 h-14 rounded-xl border p-1 bg-white shrink-0 overflow-hidden transition-all cursor-pointer ${activeImageIndex === idx
                                                    ? 'ring-2 ring-[#1AA7DE] border-[#1AA7DE] shadow-xs scale-105'
                                                    : 'border-slate-200 opacity-60 hover:opacity-100 hover:border-slate-300'
                                                    }`}
                                            >
                                                <img
                                                    src={`${henceforthApi?.API_FILE_ROOT_SMALL || henceforthApi?.API_FILE_ROOT_ORIGINAL}${img}`}
                                                    alt={`Thumbnail ${idx + 1}`}
                                                    className="w-full h-full object-contain"
                                                />
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Right Column: Product Essentials (7 cols) */}
                            <div className="lg:col-span-7 space-y-4">
                                {/* Brand & Category hierarchy tags */}
                                <div className="flex flex-wrap items-center gap-2">
                                    {state.brand_id?.name && (
                                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-xl bg-sky-50 text-[#1AA7DE] font-bold text-xs border border-sky-100 shadow-2xs">
                                            <Building2 className="w-3.5 h-3.5" />
                                            {state.brand_id.name}
                                        </span>
                                    )}

                                    <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 bg-slate-50 px-3 py-1 rounded-xl border border-slate-200/60 flex-wrap">
                                        <Tag className="w-3.5 h-3.5 text-slate-400" />
                                        <span>{state.category_id?.name || 'General'}</span>
                                        {state.subcategory_id?.name && (
                                            <>
                                                <ChevronRight className="w-3 h-3 text-slate-300" />
                                                <span>{state.subcategory_id.name}</span>
                                            </>
                                        )}
                                        {state.sub_subcategory_id?.name && (
                                            <>
                                                <ChevronRight className="w-3 h-3 text-slate-300" />
                                                <span className="text-slate-800">{state.sub_subcategory_id.name}</span>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {/* Product Name */}
                                <h1 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight leading-snug my-2">
                                    {state.name || language_data?.[authState.lang]?.common_not_available || 'Product Name'}
                                </h1>

                                {/* Price & Tax Box */}
                                <div className="p-4 rounded-2xl bg-gradient-to-br from-slate-50/90 to-slate-100/50 border border-slate-200/70 space-y-2">
                                    <div className="flex flex-wrap items-baseline gap-3">
                                        <span className="text-3xl font-black text-slate-900 tracking-tight">
                                            &#36;{numberWithCommas(discountPercent > 0 ? discountedPrice : originalPrice)}
                                        </span>

                                        {discountPercent > 0 && (
                                            <span className="text-base font-bold text-slate-400 line-through">
                                                &#36;{numberWithCommas(originalPrice)}
                                            </span>
                                        )}

                                        <span className="text-xs font-bold text-slate-500 px-2 py-0.5 rounded-lg bg-white border border-slate-200 shadow-2xs">
                                            Excl. Tax
                                        </span>

                                        {discountPercent > 0 && (
                                            <span className="text-xs font-black px-2.5 py-0.5 rounded-lg bg-emerald-100 text-emerald-800 border border-emerald-200">
                                                Save {discountPercent}%
                                            </span>
                                        )}
                                    </div>

                                    {/* Tax Note */}
                                    <div className="flex items-center gap-4 text-xs font-medium text-slate-500 pt-1 border-t border-slate-200/60">
                                        <span>
                                            Tax Rate:{' '}
                                            <strong className="text-slate-700 font-bold">
                                                {state.tax_percentage || 0}%
                                            </strong>{' '}
                                            (~&#36;{taxAmount.toFixed(2)})
                                        </span>
                                        {discountPercent > 0 && (
                                            <span>
                                                Calculated Discount:{' '}
                                                <strong className="text-emerald-700 font-bold">
                                                    -&#36;{(originalPrice - discountedPrice).toFixed(2)}
                                                </strong>
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {/* Variant Display Pill (if any) */}
                                {state.variant_type && (
                                    <div className="p-3 bg-slate-50/80 rounded-xl border border-slate-200/70 flex items-center gap-3">
                                        <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                                            {state.variant_type} Variant:
                                        </span>
                                        {state.variant_type === 'COLOR' && (
                                            <div className="flex items-center gap-2.5">
                                                <span
                                                    className="w-6 h-6 rounded-lg border border-slate-300 shadow-2xs inline-block"
                                                    style={{
                                                        backgroundColor:
                                                            state.variant_value?.color || state.variant_value?.value || '#1AA7DE',
                                                    }}
                                                />
                                                <span className="text-xs font-bold text-slate-800">
                                                    {state.variant_value?.value || state.variant_value?.label || 'Color'}
                                                </span>
                                                {state.variant_value?.color && (
                                                    <span className="font-mono text-xs font-semibold text-slate-500 px-2 py-0.5 bg-white rounded-md border border-slate-200">
                                                        {state.variant_value.color}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                        {state.variant_type === 'SIZE' && (
                                            <span className="px-3 py-1 rounded-lg bg-white border border-slate-200 text-xs font-bold text-slate-800 shadow-2xs">
                                                {state.variant_value?.label ? `${state.variant_value?.label}: ` : ''}
                                                {state.variant_value?.value || 'Size'}
                                            </span>
                                        )}
                                    </div>
                                )}

                                {/* Stock Progress & Key Metrics */}
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                    <div className="p-3 rounded-xl bg-white border border-slate-200/80 shadow-2xs space-y-1">
                                        <span className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                                            Stock Level
                                        </span>
                                        <span className="text-sm font-extrabold text-slate-800 block">
                                            {stockQuantity} Units
                                        </span>
                                        <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden mt-1">
                                            <div
                                                className={`h-full rounded-full ${stockQuantity > 10
                                                    ? 'bg-emerald-500'
                                                    : stockQuantity > 0
                                                        ? 'bg-amber-500'
                                                        : 'bg-rose-500'
                                                    }`}
                                                style={{ width: `${Math.min(100, Math.max(5, stockQuantity * 2))}%` }}
                                            />
                                        </div>
                                    </div>

                                    <div className="p-3 rounded-xl bg-white border border-slate-200/80 shadow-2xs space-y-1">
                                        <span className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                                            Product ID
                                        </span>
                                        <span className="text-sm font-mono font-bold text-slate-800 block truncate">
                                            {state.prodct_id || 'N/A'}
                                        </span>
                                        <span className="text-[10px] text-slate-400 font-medium">Unique Catalog SKU</span>
                                    </div>

                                    <div className="p-3 rounded-xl bg-white border border-slate-200/80 shadow-2xs col-span-2 sm:col-span-1 space-y-1">
                                        <span className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                                            Customer Reviews
                                        </span>
                                        <div className="flex items-center gap-1.5">
                                            <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                                            <span className="text-sm font-extrabold text-slate-800">
                                                {averageRating || 'New'}
                                            </span>
                                            <span className="text-xs text-slate-400 font-medium">
                                                ({state.ratings?.length || state.total_reviews || 0})
                                            </span>
                                        </div>
                                        <span className="text-[10px] text-slate-400 font-medium">Overall Rating</span>
                                    </div>
                                </div>

                                {/* Product Description */}
                                <div className="p-4 rounded-xl bg-slate-50/70 border border-slate-200/70 space-y-1.5">
                                    <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider m-0">
                                        {language_data?.[authState.lang]?.common_description || 'Product Description'}
                                    </h3>
                                    <div className="text-xs sm:text-sm text-slate-600 font-normal leading-relaxed">
                                        {state.description ? (
                                            <div>
                                                <p className="m-0 whitespace-pre-line">
                                                    {state.description.length > 300 && !showMoreDesc
                                                        ? `${state.description.slice(0, 300)}...`
                                                        : state.description}
                                                </p>
                                                {state.description.length > 300 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowMoreDesc(!showMoreDesc)}
                                                        className="mt-1 text-xs font-bold text-[#1AA7DE] hover:underline flex items-center gap-1 cursor-pointer"
                                                    >
                                                        {showMoreDesc
                                                            ? language_data?.[authState.lang]?.common_read_less || 'Show Less'
                                                            : language_data?.[authState.lang]?.common_read_more || 'Read More'}
                                                        {showMoreDesc ? (
                                                            <ChevronUp className="w-3.5 h-3.5" />
                                                        ) : (
                                                            <ChevronDown className="w-3.5 h-3.5" />
                                                        )}
                                                    </button>
                                                )}
                                            </div>
                                        ) : (
                                            <span className="text-slate-400 italic">No description provided</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 8-col / 4-col Split Layout for Deep Details - Spanning Full Width */}
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 w-full">
                        {/* LEFT 8 COLUMNS: Highlights, Specifications, Variations, FAQs, Reviews */}
                        <div className="lg:col-span-8 space-y-5">
                            {/* 1. Highlights Card */}
                            <SectionCard
                                id="highlights-section"
                                title={language_data?.[authState.lang]?.common_highlights || 'Product Highlights'}
                                subtitle="Bullet points displayed prominently on the buyer product page"
                                icon={<Sparkles className="w-4 h-4" />}
                                action={
                                    <Button
                                        asChild
                                        variant="default"

                                        className="gap-1"
                                    >
                                        <Link to={`/product/${productId}/highlight/add`}>
                                            <Plus className="w-3.5 h-3.5" />
                                            <span>Add Highlight</span>
                                        </Link>
                                    </Button>
                                }
                            >
                                {highlights.length > 0 ? (
                                    <div className="space-y-2">
                                        {highlights.map((item: any) => (
                                            <div
                                                key={item._id}
                                                className="flex items-center justify-between gap-3 p-3 rounded-xl bg-slate-50/70 border border-slate-100 hover:bg-white hover:border-slate-200 transition-all shadow-2xs group"
                                            >
                                                <div className="flex items-start gap-2.5 min-w-0">
                                                    <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                                                    <span className="text-xs sm:text-sm font-medium text-slate-800 leading-snug break-words">
                                                        {item.content}
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-1 shrink-0">
                                                    <Button
                                                        asChild
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-8 w-8 text-slate-400 hover:text-slate-700"
                                                    >
                                                        <Link
                                                            to={`/product/${productId}/highlight/${item._id}/edit?content=${encodeURIComponent(
                                                                item.content
                                                            )}`}
                                                            title="Edit highlight"
                                                        >
                                                            <Pencil className="w-3.5 h-3.5" />
                                                        </Link>
                                                    </Button>
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() =>
                                                            deleteSpecificDetail(commonArray.productOtherDetails[0], item._id)
                                                        }
                                                        className="h-8 w-8 text-slate-400 hover:text-destructive cursor-pointer"
                                                        title="Delete highlight"
                                                    >
                                                        <Trash2 className="w-3.5 h-3.5" />
                                                    </Button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-8 px-4 bg-slate-50/50 rounded-2xl border border-dashed border-slate-200">
                                        <Sparkles className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                                        <p className="text-xs text-slate-500 font-semibold m-0">No highlights added yet.</p>
                                        <Link
                                            to={`/product/${productId}/highlight/add`}
                                            className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-[#1AA7DE] hover:underline"
                                        >
                                            <Plus className="w-3 h-3" />
                                            Add first highlight
                                        </Link>
                                    </div>
                                )}
                            </SectionCard>

                            {/* 2. Specifications Card */}
                            <SectionCard
                                id="specifications-section"
                                title={language_data?.[authState.lang]?.common_specifications || 'Technical Specifications'}
                                subtitle="Detailed specifications displayed in table format"
                                icon={<Layers className="w-4 h-4" />}
                                action={
                                    <Button
                                        asChild
                                        variant="default"

                                        className="gap-1"
                                    >
                                        <Link to={`/product/${productId}/specification/add`}>
                                            <Plus className="w-3.5 h-3.5" />
                                            <span>Add Specification</span>
                                        </Link>
                                    </Button>
                                }
                            >
                                {specification.length > 0 ? (
                                    <div className="rounded-xl border border-slate-200/80 overflow-hidden divide-y divide-slate-100">
                                        {specification.map((item: any) => (
                                            <div
                                                key={item._id}
                                                className="flex items-center justify-between gap-3 p-3 bg-white hover:bg-slate-50/60 transition-colors"
                                            >
                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 sm:gap-4 flex-grow min-w-0">
                                                    <span className="text-xs font-bold text-slate-500 uppercase tracking-wider truncate">
                                                        {item.key}
                                                    </span>
                                                    <span className="text-xs sm:text-sm font-semibold text-slate-800 break-words">
                                                        {item.value}
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-1 shrink-0">
                                                    <Button
                                                        asChild
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-8 w-8 text-slate-400 hover:text-slate-700"
                                                    >
                                                        <Link
                                                            to={`/product/${productId}/specification/${item._id}/edit?key=${encodeURIComponent(
                                                                item.key
                                                            )}&value=${encodeURIComponent(item.value)}`}
                                                            title="Edit specification"
                                                        >
                                                            <Pencil className="w-3.5 h-3.5" />
                                                        </Link>
                                                    </Button>
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() =>
                                                            deleteSpecificDetail(commonArray.productOtherDetails[1], item._id)
                                                        }
                                                        className="h-8 w-8 text-slate-400 hover:text-destructive cursor-pointer"
                                                        title="Delete specification"
                                                    >
                                                        <Trash2 className="w-3.5 h-3.5" />
                                                    </Button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-8 px-4 bg-slate-50/50 rounded-2xl border border-dashed border-slate-200">
                                        <Layers className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                                        <p className="text-xs text-slate-500 font-semibold m-0">No specifications added yet.</p>
                                        <Link
                                            to={`/product/${productId}/specification/add`}
                                            className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-[#1AA7DE] hover:underline"
                                        >
                                            <Plus className="w-3 h-3" />
                                            Add first specification
                                        </Link>
                                    </div>
                                )}
                            </SectionCard>

                            {/* 3. Product Variations Card */}
                            <SectionCard
                                id="variations-section"
                                title={language_data?.[authState.lang]?.common_variations || 'Product Variations'}
                                subtitle="Connected child product variants (colors, sizes, configurations)"
                                icon={<Boxes className="w-4 h-4" />}
                                action={
                                    <Button
                                        asChild
                                        variant="default"

                                        className="gap-1"
                                    >
                                        <Link to={`/product/${productId}/variant/add`}>
                                            <Plus className="w-3.5 h-3.5" />
                                            <span>Add Variant</span>
                                        </Link>
                                    </Button>
                                }
                            >
                                {variants.length > 0 ? (
                                    <div className="space-y-2">
                                        {variants.map((item: any, idx: number) => (
                                            <div
                                                key={item._id || idx}
                                                className="flex items-center justify-between gap-3 p-3 rounded-xl bg-slate-50/70 border border-slate-100 hover:bg-white hover:border-slate-200 transition-all shadow-2xs"
                                            >
                                                <div className="flex items-center gap-3 min-w-0">
                                                    <div className="w-11 h-11 rounded-xl border border-slate-200 bg-white p-0.5 shrink-0 overflow-hidden flex items-center justify-center shadow-2xs">
                                                        {item.product_images && item.product_images[0] ? (
                                                            <img
                                                                src={`${henceforthApi.API_FILE_ROOT_SMALL}${item.product_images[0]}`}
                                                                alt={item.product_name}
                                                                className="w-full h-full object-contain"
                                                            />
                                                        ) : (
                                                            <Package className="w-5 h-5 text-slate-300" />
                                                        )}
                                                    </div>
                                                    <div className="min-w-0">
                                                        <Link
                                                            to={`/product/${item.product_id}`}
                                                            className="text-xs sm:text-sm font-bold text-slate-800 hover:text-[#1AA7DE] truncate block transition-colors"
                                                        >
                                                            {item.product_name || `Variant #${idx + 1}`}
                                                        </Link>
                                                        <span className="text-[11px] font-mono text-slate-400">
                                                            SKU ID: {item.product_id?.slice(-8)}
                                                        </span>
                                                    </div>
                                                </div>

                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() =>
                                                        deleteSpecificDetail(commonArray.productOtherDetails[2], item._id)
                                                    }
                                                    className="h-8 w-8 text-slate-400 hover:text-destructive cursor-pointer"
                                                    title="Delete variant"
                                                >
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                </Button>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-8 px-4 bg-slate-50/50 rounded-2xl border border-dashed border-slate-200">
                                        <Boxes className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                                        <p className="text-xs text-slate-500 font-semibold m-0">No variants linked to this product.</p>
                                    </div>
                                )}
                            </SectionCard>

                            {/* 4. Frequently Asked Questions (FAQ) Card */}
                            <SectionCard
                                id="faq-section"
                                title={language_data?.[authState.lang]?.common_faq || 'Frequently Asked Questions'}
                                subtitle="Common answers to help buyers purchase with confidence"
                                icon={<HelpCircle className="w-4 h-4" />}
                                action={
                                    <Button
                                        asChild
                                        variant="default"

                                        className="gap-1"
                                    >
                                        <Link to={`/product/${productId}/faq/add`}>
                                            <Plus className="w-3.5 h-3.5" />
                                            <span>Add FAQ</span>
                                        </Link>
                                    </Button>
                                }
                            >
                                {faq.length > 0 ? (
                                    <div className="space-y-2.5">
                                        {faq.map((item: any, idx: number) => {
                                            const isOpen = openFaqIndex === idx;
                                            return (
                                                <div
                                                    key={item._id || idx}
                                                    className="rounded-xl border border-slate-200/80 bg-white shadow-2xs overflow-hidden transition-all"
                                                >
                                                    <div className="flex items-center justify-between p-3.5 gap-3 bg-slate-50/60">
                                                        <button
                                                            type="button"
                                                            onClick={() => setOpenFaqIndex(isOpen ? null : idx)}
                                                            className="flex items-center gap-2.5 text-left flex-grow font-bold text-xs sm:text-sm text-slate-800 hover:text-[#1AA7DE] transition-colors cursor-pointer"
                                                        >
                                                            <span className="w-5 h-5 rounded-md bg-sky-100 text-[#1AA7DE] flex items-center justify-center text-xs font-black shrink-0">
                                                                Q
                                                            </span>
                                                            <span className="leading-snug">{item.question}</span>
                                                            <ChevronDown
                                                                className={`w-4 h-4 text-slate-400 shrink-0 ml-auto transition-transform duration-200 ${isOpen ? 'rotate-180 text-[#1AA7DE]' : ''
                                                                    }`}
                                                            />
                                                        </button>

                                                        <div className="flex items-center gap-1 shrink-0 ml-1">
                                                            <Button
                                                                asChild
                                                                variant="ghost"
                                                                size="icon"
                                                                className="h-8 w-8 text-slate-400 hover:text-slate-700"
                                                            >
                                                                <Link
                                                                    to={`/product/${productId}/faq/edit/${item._id}`}
                                                                    title="Edit FAQ"
                                                                >
                                                                    <Pencil className="w-3.5 h-3.5" />
                                                                </Link>
                                                            </Button>
                                                            <Button
                                                                type="button"
                                                                variant="ghost"
                                                                size="icon"
                                                                onClick={() =>
                                                                    deleteSpecificDetail(commonArray.productOtherDetails[3], item._id)
                                                                }
                                                                className="h-8 w-8 text-slate-400 hover:text-destructive cursor-pointer"
                                                                title="Delete FAQ"
                                                            >
                                                                <Trash2 className="w-3.5 h-3.5" />
                                                            </Button>
                                                        </div>
                                                    </div>

                                                    {isOpen && (
                                                        <div className="p-4 pt-3 border-t border-slate-100 bg-white text-xs sm:text-sm text-slate-600 leading-relaxed">
                                                            <div className="flex items-start gap-2.5">
                                                                <span className="w-5 h-5 rounded-md bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-black shrink-0 mt-0.5">
                                                                    A
                                                                </span>
                                                                <div className="flex-grow">
                                                                    <div
                                                                        dangerouslySetInnerHTML={{
                                                                            __html:
                                                                                item.answer?.length > 300 && !item.readMore
                                                                                    ? `${item.answer.slice(0, 300)}...`
                                                                                    : item.answer || '',
                                                                        }}
                                                                    />
                                                                    {item.answer?.length > 300 && (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => toggleFaqReadMore(idx)}
                                                                            className="text-xs font-bold text-[#1AA7DE] hover:underline mt-1.5 block cursor-pointer"
                                                                        >
                                                                            {item.readMore ? 'Show Less' : 'Read More'}
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="text-center py-8 px-4 bg-slate-50/50 rounded-2xl border border-dashed border-slate-200">
                                        <HelpCircle className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                                        <p className="text-xs text-slate-500 font-semibold m-0">No FAQs added yet.</p>
                                    </div>
                                )}
                            </SectionCard>

                            {/* 5. Ratings & Customer Reviews Card */}
                            <SectionCard
                                id="ratings-section"
                                title={language_data?.[authState.lang]?.common_rating_reviews || 'Ratings & Reviews'}
                                subtitle="Customer feedback and satisfaction ratings"
                                icon={<Star className="w-4 h-4 text-amber-500" />}
                            >
                                {Array.isArray(state.ratings) && state.ratings.length > 0 ? (
                                    <div className="space-y-4">
                                        {/* Visual Rating Breakdown */}
                                        <div className="p-4 rounded-2xl bg-slate-50/80 border border-slate-200/70 grid grid-cols-1 sm:grid-cols-12 gap-4 items-center">
                                            <div className="sm:col-span-4 text-center sm:border-r border-slate-200/80 sm:pr-4">
                                                <span className="text-4xl font-black text-slate-900 tracking-tight block">
                                                    {averageRating || '0.0'}
                                                </span>
                                                <div className="flex items-center justify-center gap-1 my-1">
                                                    {[1, 2, 3, 4, 5].map((s) => (
                                                        <Star
                                                            key={s}
                                                            className={`w-4 h-4 ${s <= Math.round(Number(averageRating || 0))
                                                                ? 'fill-amber-400 text-amber-400'
                                                                : 'text-slate-200 fill-slate-200'
                                                                }`}
                                                        />
                                                    ))}
                                                </div>
                                                <span className="text-xs text-slate-500 font-semibold">
                                                    Based on {state.ratings.length} verified reviews
                                                </span>
                                            </div>

                                            {/* 5 to 1 Star Progress Bars */}
                                            <div className="sm:col-span-8 space-y-1.5">
                                                {[5, 4, 3, 2, 1].map((stars) => {
                                                    const count = ratingCounts[stars as 1 | 2 | 3 | 4 | 5] || 0;
                                                    const pct = state.ratings.length ? (count / state.ratings.length) * 100 : 0;
                                                    return (
                                                        <div key={stars} className="flex items-center gap-2 text-xs">
                                                            <span className="font-bold text-slate-600 w-6 flex items-center gap-0.5 shrink-0">
                                                                {stars} <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                                                            </span>
                                                            <div className="flex-grow h-2 bg-slate-200/70 rounded-full overflow-hidden">
                                                                <div
                                                                    className="h-full bg-amber-400 rounded-full transition-all duration-300"
                                                                    style={{ width: `${pct}%` }}
                                                                />
                                                            </div>
                                                            <span className="font-mono text-slate-400 w-6 text-right shrink-0">
                                                                {count}
                                                            </span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        {/* Individual Reviews Feed */}
                                        <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
                                            {state.ratings.map((rev: any, index: number) => {
                                                const userObj = rev?.user || rev?.user_id || {};
                                                const userName =
                                                    userObj?.name ||
                                                    rev?.user_name ||
                                                    rev?.username ||
                                                    language_data?.[authState.lang]?.common_not_available ||
                                                    'Customer';
                                                const userPic = userObj?.profile_pic || rev?.profile_pic;

                                                return (
                                                    <div
                                                        key={index}
                                                        className="p-3.5 rounded-xl bg-white border border-slate-100 shadow-2xs space-y-2 hover:border-slate-200 transition-all"
                                                    >
                                                        <div className="flex items-center justify-between gap-2">
                                                            <div className="flex items-center gap-2.5">
                                                                <img
                                                                    src={
                                                                        userPic
                                                                            ? `${henceforthApi.API_FILE_ROOT_MEDIUM}${userPic}`
                                                                            : profile_img1
                                                                    }
                                                                    alt={userName}
                                                                    className="w-8 h-8 rounded-full object-cover border border-slate-200"
                                                                />
                                                                <div>
                                                                    <h5 className="text-xs font-bold text-slate-800 m-0 leading-none">
                                                                        {userName}
                                                                    </h5>
                                                                    <div className="flex items-center gap-0.5 mt-1">
                                                                        {[1, 2, 3, 4, 5].map((s) => (
                                                                            <Star
                                                                                key={s}
                                                                                className={`w-2.5 h-2.5 ${s <= (rev?.ratings || 0)
                                                                                    ? 'fill-amber-400 text-amber-400'
                                                                                    : 'text-slate-200 fill-slate-200'
                                                                                    }`}
                                                                            />
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <span className="text-[11px] text-slate-400 font-medium">
                                                                {rev?.created_at
                                                                    ? moment(Number(rev.created_at)).format('MMM D, YYYY')
                                                                    : ''}
                                                            </span>
                                                        </div>

                                                        {rev?.title && (
                                                            <h6 className="text-xs font-bold text-slate-900 m-0">{rev.title}</h6>
                                                        )}

                                                        {rev?.description && (
                                                            <p className="text-xs text-slate-600 font-normal leading-relaxed m-0">
                                                                {rev.description}
                                                            </p>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-center py-8 px-4 bg-slate-50/50 rounded-2xl border border-dashed border-slate-200">
                                        <Star className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                                        <p className="text-xs text-slate-500 font-semibold m-0">No customer reviews yet.</p>
                                    </div>
                                )}
                            </SectionCard>
                        </div>

                        {/* RIGHT 4 COLUMNS: Sidebar (Delivery Zones, Services, Organization, Status) */}
                        <div className="lg:col-span-4 space-y-5">
                            {/* Shipping & Delivery Zones Card */}
                            <SectionCard
                                id="delivery-section"
                                title={language_data?.[authState.lang]?.common_delivery || 'Shipping & Delivery'}
                                subtitle="Worldwide coverage and custom local delivery zones"
                                icon={<Truck className="w-4 h-4" />}
                                action={
                                    <Button
                                        asChild
                                        variant="default"

                                        className="gap-1"
                                    >
                                        <Link to={`/product/${productId}/delivery/add`}>
                                            <Plus className="w-3.5 h-3.5" />
                                            <span>Add Zone</span>
                                        </Link>
                                    </Button>
                                }
                            >
                                <div className="space-y-3">
                                    {/* Global Shipping Toggle Card */}
                                    <div className="p-3 rounded-xl bg-slate-50/80 border border-slate-200/70 flex items-center justify-between gap-3">
                                        <div className="flex items-center gap-2.5 min-w-0">
                                            <div className="w-8 h-8 rounded-lg bg-sky-50 text-[#1AA7DE] flex items-center justify-center shrink-0">
                                                <Globe className="w-4 h-4" />
                                            </div>
                                            <div className="min-w-0">
                                                <h4 className="text-xs font-bold text-slate-800 m-0 truncate">Worldwide Delivery</h4>
                                                <span className="text-[11px] text-slate-500 font-medium block">
                                                    {state.is_delivery_available ? 'Active globally' : 'Disabled'}
                                                </span>
                                            </div>
                                        </div>
                                        <label className="relative inline-flex items-center cursor-pointer shrink-0">
                                            <input
                                                type="checkbox"
                                                checked={state.is_delivery_available}
                                                onChange={(e) => toggleDelivery(e.target.checked)}
                                                disabled={isTogglingDelivery}
                                                className="sr-only peer"
                                            />
                                            <div className="w-10 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#1AA7DE]"></div>
                                        </label>
                                    </div>

                                    {/* Configured Local Zones */}
                                    {delivery.length > 0 ? (
                                        <div className="space-y-2">
                                            <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block">
                                                Configured Local Zones ({delivery.length})
                                            </span>
                                            {delivery.map((item: any, idx: number) => (
                                                <div
                                                    key={item._id || idx}
                                                    className="p-2.5 rounded-xl bg-white border border-slate-100 shadow-2xs space-y-1"
                                                >
                                                    <div className="flex items-center justify-between gap-2">
                                                        <span className="text-xs font-bold text-slate-800 flex items-center gap-1">
                                                            <MapPin className="w-3.5 h-3.5 text-slate-400" />
                                                            {item.radius ? `${item.radius} ${item.units || 'km'}` : 'Location Zone'}
                                                        </span>
                                                        <div className="flex items-center gap-1">
                                                            <Button
                                                                asChild
                                                                variant="ghost"
                                                                size="icon"
                                                                className="h-7 w-7 text-slate-400 hover:text-slate-700"
                                                            >
                                                                <Link
                                                                    to={`/product/${productId}/delivery/edit/${item._id}`}
                                                                    title="Edit zone"
                                                                >
                                                                    <Pencil className="w-3 h-3" />
                                                                </Link>
                                                            </Button>
                                                            <Button
                                                                type="button"
                                                                variant="ghost"
                                                                size="icon"
                                                                onClick={() =>
                                                                    deleteSpecificDetail(commonArray.productOtherDetails[5], item._id)
                                                                }
                                                                className="h-7 w-7 text-slate-400 hover:text-destructive cursor-pointer"
                                                                title="Delete zone"
                                                            >
                                                                <Trash2 className="w-3 h-3" />
                                                            </Button>
                                                        </div>
                                                    </div>
                                                    {item.address && (
                                                        <p className="text-[11px] text-slate-500 m-0 truncate">{item.address}</p>
                                                    )}
                                                    {item.delivery_time && (
                                                        <span className="inline-flex items-center gap-1 text-[10px] font-bold text-sky-700 bg-sky-50 px-2 py-0.5 rounded-md">
                                                            <Clock className="w-3 h-3" />
                                                            {item.delivery_time}
                                                        </span>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-center py-4 px-3 bg-slate-50/50 rounded-xl border border-dashed border-slate-200">
                                            <p className="text-xs text-slate-500 font-medium m-0">No specific delivery zones added</p>
                                        </div>
                                    )}
                                </div>
                            </SectionCard>

                            {/* Services & Warranty Card */}
                            <SectionCard
                                id="services-section"
                                title={language_data?.[authState.lang]?.common_services || 'Services & Warranty'}
                                subtitle="Buyer guarantees, warranties and support"
                                icon={<ShieldCheck className="w-4 h-4" />}
                                action={
                                    <Button
                                        asChild
                                        variant="default"

                                        className="gap-1"
                                    >
                                        <Link to={`/product/${productId}/services/add`}>
                                            <Plus className="w-3.5 h-3.5" />
                                            <span>Add Service</span>
                                        </Link>
                                    </Button>
                                }
                            >
                                {services.length > 0 ? (
                                    <div className="space-y-2">
                                        {services.map((item: any) => (
                                            <div
                                                key={item._id}
                                                className="flex items-center justify-between gap-2.5 p-2.5 rounded-xl bg-slate-50/70 border border-slate-100 hover:bg-white hover:border-slate-200 transition-all shadow-2xs"
                                            >
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <div className="w-7 h-7 rounded-lg bg-sky-50 text-[#1AA7DE] flex items-center justify-center shrink-0">
                                                        <ShieldCheck className="w-3.5 h-3.5" />
                                                    </div>
                                                    <span className="text-xs font-semibold text-slate-800 leading-snug break-words">
                                                        {item.content}
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-1 shrink-0">
                                                    <Button
                                                        asChild
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-7 w-7 text-slate-400 hover:text-slate-700"
                                                    >
                                                        <Link
                                                            to={`/product/${productId}/services/edit/${item._id}/?service=${encodeURIComponent(
                                                                item.content
                                                            )}`}
                                                            title="Edit service"
                                                        >
                                                            <Pencil className="w-3 h-3" />
                                                        </Link>
                                                    </Button>
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() =>
                                                            deleteSpecificDetail(commonArray.productOtherDetails[4], item._id)
                                                        }
                                                        className="h-7 w-7 text-slate-400 hover:text-destructive cursor-pointer"
                                                        title="Delete service"
                                                    >
                                                        <Trash2 className="w-3 h-3" />
                                                    </Button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-6 px-4 bg-slate-50/50 rounded-xl border border-dashed border-slate-200">
                                        <ShieldCheck className="w-6 h-6 text-slate-300 mx-auto mb-1.5" />
                                        <p className="text-xs text-slate-500 font-medium m-0">No services added yet.</p>
                                    </div>
                                )}
                            </SectionCard>

                            {/* Categorization & Organization Card */}
                            <SectionCard
                                title="Product Categorization"
                                subtitle="Catalog organization hierarchy"
                                icon={<Building2 className="w-4 h-4" />}
                            >
                                <div className="space-y-3 text-xs">
                                    <div className="flex items-center justify-between py-1.5 border-b border-slate-100">
                                        <span className="text-slate-400 font-bold uppercase tracking-wider text-[11px]">Brand</span>
                                        <span className="font-bold text-slate-800">{state.brand_id?.name || 'Unbranded'}</span>
                                    </div>
                                    <div className="flex items-center justify-between py-1.5 border-b border-slate-100">
                                        <span className="text-slate-400 font-bold uppercase tracking-wider text-[11px]">Category L1</span>
                                        <span className="font-bold text-slate-800">{state.category_id?.name || 'N/A'}</span>
                                    </div>
                                    <div className="flex items-center justify-between py-1.5 border-b border-slate-100">
                                        <span className="text-slate-400 font-bold uppercase tracking-wider text-[11px]">Subcategory L2</span>
                                        <span className="font-bold text-slate-800">{state.subcategory_id?.name || 'N/A'}</span>
                                    </div>
                                    {state.sub_subcategory_id?.name && (
                                        <div className="flex items-center justify-between py-1.5 border-b border-slate-100">
                                            <span className="text-slate-400 font-bold uppercase tracking-wider text-[11px]">Subcategory L3</span>
                                            <span className="font-bold text-slate-800">{state.sub_subcategory_id.name}</span>
                                        </div>
                                    )}
                                    <div className="flex items-center justify-between pt-1">
                                        <span className="text-slate-400 font-bold uppercase tracking-wider text-[11px]">Catalog Type</span>
                                        <span className="font-bold text-[#1AA7DE] bg-sky-50 px-2 py-0.5 rounded-md">
                                            {state.product_type || 'Standard'}
                                        </span>
                                    </div>
                                </div>
                            </SectionCard>
                        </div>
                    </div>
                </div>
            )}

            {/* High-Res Image Lightbox Modal */}
            {isLightboxOpen && (
                <div
                    role="dialog"
                    aria-modal="true"
                    className="fixed inset-0 z-50 bg-slate-900/80 backdrop-blur-sm flex items-center justify-center p-4"
                    onClick={() => setIsLightboxOpen(false)}
                >
                    <div
                        className="relative max-w-4xl max-h-[90vh] bg-white rounded-3xl p-4 sm:p-6 shadow-2xl overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            onClick={() => setIsLightboxOpen(false)}
                            className="absolute top-4 right-4 z-10 rounded-full"
                            title="Close preview"
                        >
                            <X className="w-5 h-5" />
                        </Button>

                        <div className="aspect-square max-h-[70vh] flex items-center justify-center overflow-hidden">
                            <img
                                src={activeImageUrl}
                                alt={state.name || 'Product Fullscreen'}
                                className="w-full h-full object-contain"
                            />
                        </div>

                        {imagesList.length > 1 && (
                            <div className="flex items-center justify-center gap-2 pt-4 border-t border-slate-100 overflow-x-auto">
                                {imagesList.map((img, idx) => (
                                    <button
                                        key={idx}
                                        type="button"
                                        onClick={() => setActiveImageIndex(idx)}
                                        className={`w-12 h-12 rounded-xl border p-1 bg-white shrink-0 overflow-hidden cursor-pointer transition-all ${activeImageIndex === idx
                                            ? 'ring-2 ring-[#1AA7DE] border-[#1AA7DE] scale-105'
                                            : 'opacity-60 hover:opacity-100'
                                            }`}
                                    >
                                        <img
                                            src={`${henceforthApi?.API_FILE_ROOT_SMALL || henceforthApi?.API_FILE_ROOT_ORIGINAL}${img}`}
                                            alt={`Thumb ${idx + 1}`}
                                            className="w-full h-full object-contain"
                                        />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </Fragment>
    );
};

export default ViewProductDetail;